import { useEffect, useState, useRef, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { usePaystackPayment } from 'react-paystack';
import { useFlutterwave, closePaymentModal } from 'flutterwave-react-v3';
import { LoadingSpinner, Card, Button, Alert } from '@/components/ui';
import type { CreateDonationResponse, PaymentGateway } from '@/types/donation';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

interface PaymentHandlerProps {
  response: CreateDonationResponse;
  gateway: PaymentGateway;
  onSuccess: (data: any) => void;
  onCancel: () => void;
  onError: (error: any) => void;
}

/**
 * Stripe Payment Form Component
 */
function StripeForm({ onCancel, onSuccess, onError }: {
  onCancel: () => void;
  onSuccess: (data: any) => void;
  onError: (error: any) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/callback`,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || 'An unexpected error occurred.');
      setIsProcessing(false);
      onError(error);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />
      {errorMessage && (
        <Alert variant="error" title="Payment Error">
          {errorMessage}
        </Alert>
      )}
      <div className="flex gap-4">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onCancel}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          className="flex-1 bg-gold text-dark"
          isLoading={isProcessing}
          disabled={!stripe || isProcessing}
        >
          Pay Now
        </Button>
      </div>
    </form>
  );
}

/**
 * Main Payment Handler Component
 */
export default function PaymentHandler({ response, gateway, onSuccess, onCancel, onError }: PaymentHandlerProps) {
  const { paymentMetadata, donation } = response.data;
  const hasInitiated = useRef(false);

  // Paystack Configuration
  const paystackConfig = useMemo(() => {
    // Try to extract access code from paymentUrl if not in metadata
    let accessCode = paymentMetadata?.accessCode;

    if (!accessCode && response.data.paymentUrl && response.data.paymentUrl.includes('paystack')) {
      // Robust extraction: get the last part of the URL path
      try {
        const url = new URL(response.data.paymentUrl);
        const parts = url.pathname.split('/');
        const lastPart = parts[parts.length - 1];
        if (lastPart && lastPart.length > 5) { // Basic length check for access code
          accessCode = lastPart;
        }
      } catch (e) {
        // Fallback to regex if URL parsing fails or if not a full URL
        const match = response.data.paymentUrl.match(/checkout\.(?:paystack\.com|paystack\.co)\/([a-zA-Z0-9]+)/);
        if (match && match[1]) {
          accessCode = match[1];
        }
      }
    }

    // CRITICAL: react-paystack needs a reference for callbacks to fire
    // If backend didn't provide one, generate from donation ID
    const reference = paymentMetadata?.reference || `donation_${donation.id}_${Date.now()}`;

    return {
      reference,
      access_code: accessCode,
      email: response.data.donor.email,
      amount: donation.amount, // amount in kobo/cents
      publicKey: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
      currency: donation.currency,
    };
  }, [paymentMetadata, response.data.donor, response.data.paymentUrl, donation]);

  const initializePaystack = usePaystackPayment(paystackConfig);

  // Flutterwave Configuration
  const flutterwaveConfig = useMemo(() => {
    // Extract payment link from Flutterwave checkout URL if needed
    let paymentLink = paymentMetadata?.payment_link;

    if (!paymentLink && response.data.paymentUrl && response.data.paymentUrl.includes('flutterwave')) {
      // Extract payment link from URL like: https://checkout.flutterwave.com/v3/hosted/pay/xxxxx
      const match = response.data.paymentUrl.match(/flutterwave\.com\/(.+)/);
      if (match && match[1]) {
        paymentLink = response.data.paymentUrl;
      }
    }

    // Generate tx_ref if not provided (similar to Paystack reference)
    const tx_ref = paymentMetadata?.reference || `donation_${donation.id}_${Date.now()}`;

    return {
      public_key: import.meta.env.VITE_FLUTTERWAVE_PUBLIC_KEY,
      tx_ref,
      payment_link: paymentLink,
      amount: donation.amount / 100,
      currency: donation.currency,
      payment_options: 'card,mobilemoney,ussd',
      customer: {
        email: response.data.donor.email,
        phone_number: response.data.donor.phone || '',
        name: `${response.data.donor.firstName || ''} ${response.data.donor.lastName || ''}`.trim(),
      },
      customizations: {
        title: 'Haapu Donation',
        description: `Donation for ${donation.id}`,
        logo: `${window.location.origin}/logo.png`,
      },
    };
  }, [paymentMetadata, response.data.donor, response.data.paymentUrl, donation]);

  const handleFlutterwavePayment = useFlutterwave(flutterwaveConfig);

  // Seerbit Configuration
  const seerbitConfig = useMemo(() => {
    const reference = paymentMetadata?.reference || `donation_${donation.id}_${Date.now()}`;

    return {
      public_key: import.meta.env.VITE_SEERBIT_PUBLIC_KEY,
      tranref: reference,
      email: response.data.donor.email,
      amount: (donation.amount / 100).toString(), // Convert from cents to main unit as string
      currency: donation.currency,
      country: donation.country || 'NG',
      full_name: `${response.data.donor.firstName || ''} ${response.data.donor.lastName || ''}`.trim() || response.data.donor.email,
    };
  }, [paymentMetadata, response.data.donor, donation]);

  useEffect(() => {
    if (hasInitiated.current) return;

    // Safety check - if no payment metadata/config, we can't process payment
    // For Paystack, we can proceed if we have an access code extracted from URL
    // For Flutterwave, we can proceed if we have a payment_link extracted from URL
    const hasPaystackAccessCode = gateway === 'paystack' && !!paystackConfig.access_code;
    const hasFlutterwavePaymentLink = gateway === 'flutterwave' && !!flutterwaveConfig.payment_link;

    if (!paymentMetadata && !hasPaystackAccessCode && !hasFlutterwavePaymentLink && gateway !== 'stripe' && gateway !== 'seerbit') {
      // If we are here, we failed to get config for inline payment
      if (gateway === 'paystack') {
        console.error('Failed to extract Paystack access code', response);
        onError(new Error('Could not initialize payment. Please contact support.'));
        return;
      }

      if (gateway === 'flutterwave') {
        console.error('Failed to extract Flutterwave payment link', response);
        onError(new Error('Could not initialize payment. Please contact support.'));
        return;
      }

      // For other scenarios, if we have a paymentUrl, redirect as fallback
      if (response.data.paymentUrl) {
        window.location.href = response.data.paymentUrl;
        return;
      }

      console.error('Missing payment metadata', response);
      onError(new Error('Missing payment configuration'));
      return;
    }

    hasInitiated.current = true;

    if (gateway === 'paystack') {
      console.log('Initializing Paystack with config:', paystackConfig);

      // When using access_code, react-paystack has limitations
      // Use direct Paystack Inline JS for better callback handling
      if (paystackConfig.access_code) {
        console.log('Using direct Paystack inline with access_code');

        // @ts-ignore - PaystackPop is loaded globally
        if (typeof window.PaystackPop === 'undefined') {
          console.error('Paystack script not loaded');
          onError(new Error('Payment system not ready. Please refresh the page.'));
          return;
        }

        let paymentCompleted = false;

        // @ts-ignore
        const handler = window.PaystackPop.setup({
          key: paystackConfig.publicKey,
          email: paystackConfig.email,
          amount: paystackConfig.amount,
          currency: paystackConfig.currency,
          ref: paystackConfig.reference,
          access_code: paystackConfig.access_code,
          // When using access_code, use 'callback' instead of 'onSuccess'
          callback: (response: any) => {
            console.log('Paystack callback triggered:', response);
            paymentCompleted = true;
            try {
              onSuccess(response);
            } catch (e) {
              console.error('Error in onSuccess callback:', e);
              onError(e);
            }
          },
          onClose: () => {
            console.log('Paystack popup closed, payment completed:', paymentCompleted);
            if (!paymentCompleted) {
              // User closed popup without completing payment
              onCancel();
            }
          },
        });

        handler.openIframe();
      } else {
        // Use react-paystack for standard flow (without access_code)
        const handlePaystackSuccess = (response: any) => {
          console.log('Paystack success callback triggered:', response);
          try {
            onSuccess(response);
          } catch (e) {
            console.error('Error in onSuccess callback:', e);
            onError(e);
          }
        };

        const handlePaystackClose = () => {
          console.log('Paystack close callback triggered');
          try {
            onCancel();
          } catch (e) {
            console.error('Error in onCancel callback:', e);
          }
        };

        // @ts-ignore - Argument mismatch in some react-paystack versions
        initializePaystack(handlePaystackSuccess, handlePaystackClose);
      }
    } else if (gateway === 'flutterwave') {
      handleFlutterwavePayment({
        callback: (res: any) => {
          closePaymentModal();
          onSuccess(res);
        },
        onClose: () => onCancel(),
      });
    } else if (gateway === 'seerbit') {
      console.log('Initializing Seerbit with config:', seerbitConfig);

      // Use window.SeerbitPay directly (loaded via script in index.html)
      // This is the same pattern as Paystack's window.PaystackPop
      // @ts-ignore - SeerbitPay is loaded globally
      if (typeof window.SeerbitPay === 'undefined') {
        console.error('Seerbit script not loaded');
        onError(new Error('Payment system not ready. Please refresh the page.'));
        return;
      }

      // @ts-ignore
      window.SeerbitPay(seerbitConfig,
        (response: any) => {
          console.log('Seerbit payment successful:', response);
          onSuccess(response);
        },
        () => {
          console.log('Seerbit modal closed');
          onCancel();
        }
      );
    } else if (gateway === 'stripe' && paymentMetadata?.sessionId) {
      // Handle Stripe Checkout Session initiation
      const redirectToStripe = async () => {
        const stripe = await stripePromise;
        if (stripe) {
          // @ts-ignore - redirectToCheckout exists on Stripe but might have type issues in some versions
          const { error } = await stripe.redirectToCheckout({
            sessionId: paymentMetadata.sessionId as string,
          });
          if (error) onError(error);
        }
      };
      redirectToStripe();
    }
  }, [gateway, paymentMetadata?.reference, paymentMetadata?.accessCode, paymentMetadata?.sessionId, paystackConfig.access_code, initializePaystack, handleFlutterwavePayment, seerbitConfig, onSuccess, onCancel, onError]);

  // If it's Stripe and we have a clientSecret, show the PaymentElement
  if (gateway === 'stripe' && paymentMetadata?.clientSecret) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <Card className="w-full max-w-lg bg-dark-card border-gold/30 max-h-[90vh] flex flex-col overflow-hidden">
          <div className="p-6 overflow-y-auto">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-white mb-2">Complete Your Donation</h3>
              <p className="text-gray-400 text-sm">Please provide your payment details to finalize your donation.</p>
            </div>
            <Elements stripe={stripePromise} options={{ clientSecret: paymentMetadata.clientSecret as string, appearance: { theme: 'night' } }}>
              <StripeForm
                onCancel={onCancel}
                onSuccess={onSuccess}
                onError={onError}
              />
            </Elements>
          </div>
        </Card>
      </div>
    );
  }

  // Show loading state while generic popups (Paystack/Flutterwave/Stripe Checkout) are initiating
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="text-center p-8 bg-dark-card border border-gold/20 rounded-2xl max-w-sm shadow-2xl">
        <LoadingSpinner size="lg" message={`Opening ${gateway}...`} />
        <p className="mt-4 text-gray-300 text-sm">Please complete the payment in the secure window that appears.</p>
        <button
          onClick={onCancel}
          className="mt-6 text-gold hover:text-gold-light hover:underline text-sm font-medium"
        >
          Cancel and go back
        </button>
      </div>
    </div>
  );
}
